import StickyFooter from '@/components/common/StickyFooter';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAccountFormSubmissionApi } from '@/hooks/useAccountForm';
import { createBillingAddressSchema } from '@/hooks/useBillingAddressForm';
import { useFormsTranslation } from '@/hooks/useTranslation';
import {
  adaptAccountFormDataForApi,
  CombinedAccountFormData,
} from '@/lib/accountFormAdapter';
import {
  createAccountGeneralInfoSchema,
  createBankConnectionSchema,
} from '@/lib/accountFormValidators';
import {
  AccountFormData,
  BankConnectionsFormData,
  BillingAddressFormData,
  ContactPartner,
  ContactPartnerFormData,
  ContractPartner,
  ContractPartnerFormData,
} from '@/types';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import AccountGeneralInfoForm, {
  AccountGeneralInfoFormRef,
} from './AccountGeneralInfoForm';
import BankConnectionForm, {
  BankConnectionFormRef,
} from './BankConnectionForm';
import BillingAddressForm, {
  BillingAddressFormRef,
} from './BillingAddressForm';
import ContactPartnerManager from './ContactPartnerManager';
import ContractPartnerManager from './ContractPartnerManager';

interface AccountFormTabsProps {
  token: string;
  onSubmit?: (data: CombinedAccountFormData) => void;
  onError?: (error: Error) => void;
  className?: string;
  // Validation states for each tab
  generalValidationState?: 'valid' | 'invalid' | 'pending';
  billingValidationState?: 'valid' | 'invalid' | 'pending';
  bankingValidationState?: 'valid' | 'invalid' | 'pending';
  contractValidationState?: 'valid' | 'invalid' | 'pending';
  contactValidationState?: 'valid' | 'invalid' | 'pending';
}

export function AccountFormTabs({
  token,
  onSubmit,
  onError,
  className,
  generalValidationState = 'pending',
  billingValidationState = 'pending',
  bankingValidationState = 'pending',
  contractValidationState = 'pending',
  contactValidationState = 'pending',
}: AccountFormTabsProps) {
  const [activeTab, setActiveTab] = useState('general');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Combined form data state for persistence across tabs
  const [formData, setFormData] = useState<CombinedAccountFormData>({
    general: {},
    billing: {},
    banking: { bankConnections: [] },
    contractPartners: [],
    contactPartners: [],
  });

  // API form submission hook
  const accountFormSubmission = useAccountFormSubmissionApi();

  // Translation hooks
  const { t: tForms } = useFormsTranslation();
  const { toast } = useToast();

  const generalFormRef = useRef<AccountGeneralInfoFormRef | null>(null);
  const billingFormRef = useRef<BillingAddressFormRef | null>(null);
  const bankingFormRef = useRef<BankConnectionFormRef | null>(null);

  // Get validation icon for a tab
  const getValidationIcon = (
    validationState: 'valid' | 'invalid' | 'pending'
  ) => {
    switch (validationState) {
      case 'valid':
        return <CheckCircle2 className='h-4 w-4 text-green-600' />;
      case 'invalid':
        return <XCircle className='h-4 w-4 text-red-600' />;
      default:
        return null;
    }
  };

  // Function to check if a form is complete using schema validation
  const isFormComplete = useCallback(
    (formType: string, data: CombinedAccountFormData): boolean => {
      if (!data) return false;

      const tFormsFunc = (key: string) => tForms(key);

      try {
        switch (formType) {
          case 'general': {
            if (!data.general) return false;
            const schema = createAccountGeneralInfoSchema(tFormsFunc);
            schema.parse(data.general);
            return true;
          }
          case 'billing': {
            if (!data.billing) return false;
            const schema = createBillingAddressSchema(tFormsFunc);
            schema.parse(data.billing);
            return true;
          }
          case 'banking': {
            if (!data.banking) return false;
            const schema = createBankConnectionSchema(tFormsFunc);
            schema.parse(data.banking);
            return true;
          }
          case 'contract': {
            // Validate that at least one contract partner exists
            if (!data.contractPartners || data.contractPartners.length === 0) {
              return false;
            }
            // Validate each contract partner has required fields
            return data.contractPartners.every(
              partner =>
                partner.firstName?.trim() &&
                partner.lastName?.trim() &&
                partner.email?.trim() &&
                partner.role &&
                partner.language
            );
          }
          case 'contact': {
            // Basic validation for contact partners
            return true;
          }
          default:
            return true;
        }
      } catch {
        return false;
      }
    },
    [tForms]
  );

  // Compute validation states based on form completion
  const getComputedValidationState = useCallback(
    (formType: string): 'valid' | 'invalid' | 'pending' => {
      if (!formData) return 'pending';
      const isComplete = isFormComplete(formType, formData);
      return isComplete ? 'valid' : 'invalid';
    },
    [formData, isFormComplete]
  );

  // Get effective validation state (prop or computed)
  const getEffectiveValidationState = useCallback(
    (
      propState: 'valid' | 'invalid' | 'pending',
      formType: string
    ): 'valid' | 'invalid' | 'pending' => {
      if (propState !== 'pending') {
        return propState;
      }
      return getComputedValidationState(formType);
    },
    [getComputedValidationState]
  );

  // Real-time change handlers for form persistence
  const handleGeneralInfoChange = useCallback(
    (data: Partial<AccountFormData>) => {
      setFormData(prev => ({
        ...prev,
        general: {
          ...prev.general,
          ...data,
        },
      }));
    },
    []
  );

  const handleBillingAddressChange = useCallback(
    (data: Partial<BillingAddressFormData>) => {
      setFormData(prev => ({
        ...prev,
        billing: {
          ...prev.billing,
          ...data,
        },
      }));
    },
    []
  );

  const handleBankingChange = useCallback((data: BankConnectionsFormData) => {
    setFormData(prev => ({
      ...prev,
      banking: data,
    }));
  }, []);

  const handleTabSubmit = (
    data:
      | AccountFormData
      | Partial<AccountFormData>
      | Partial<BillingAddressFormData>
      | BankConnectionsFormData
  ) => {
    // Update the formData based on the active tab
    if (activeTab === 'general') {
      const updatedFormData = {
        ...formData,
        general: data as Partial<AccountFormData>,
      };
      setFormData(updatedFormData);
      if (onSubmit) {
        onSubmit(updatedFormData);
      }
    } else if (activeTab === 'billing') {
      const updatedFormData = {
        ...formData,
        billing: data as Partial<BillingAddressFormData>,
      };
      setFormData(updatedFormData);
    } else if (activeTab === 'banking') {
      const updatedFormData = {
        ...formData,
        banking: data as BankConnectionsFormData,
      };
      setFormData(updatedFormData);
      handleBankConnectionSubmit(data as BankConnectionsFormData);
    }
  };

  const handleTabError = (error: Error) => {
    onError?.(error);
  };

  const handleBankConnectionSubmit = async (_data: BankConnectionsFormData) => {
    setIsLoading(true);
    try {
      // Simulate successful submission
      setTimeout(() => {
        setIsLoading(false);
        // Optionally show success message or navigate to next step
      }, 1000);
    } catch (error) {
      setIsLoading(false);
      onError?.(error as Error);
    }
  };

  // Contract Partner handlers
  const handleAddContractPartner = async (data: ContractPartnerFormData) => {
    setIsLoading(true);
    try {
      // Generate a temporary ID for the new contract partner
      const newPartner: ContractPartner = {
        id: `temp-${Date.now()}`,
        ...data,
      };
      const updatedPartners = [
        ...(formData.contractPartners || []),
        newPartner,
      ];
      setFormData(prev => ({ ...prev, contractPartners: updatedPartners }));
    } catch (error) {
      console.error('Error adding contract partner:', error);
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditContractPartner = async (
    id: string,
    data: ContractPartnerFormData
  ) => {
    setIsLoading(true);
    try {
      const updatedPartners = (formData.contractPartners || []).map(partner =>
        partner.id === id ? { ...partner, ...data } : partner
      );
      setFormData(prev => ({ ...prev, contractPartners: updatedPartners }));
    } catch (error) {
      console.error('Error editing contract partner:', error);
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteContractPartner = async (id: string) => {
    setIsLoading(true);
    try {
      const updatedPartners = (formData.contractPartners || []).filter(
        partner => partner.id !== id
      );
      setFormData(prev => ({ ...prev, contractPartners: updatedPartners }));
    } catch (error) {
      console.error('Error deleting contract partner:', error);
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  // Contact Partner handlers
  const handleAddContactPartner = async (data: ContactPartnerFormData) => {
    setIsLoading(true);
    try {
      // Generate a temporary ID for the new contact partner
      const newPartner: ContactPartner = {
        id: `temp-${Date.now()}`,
        ...data,
      };
      const updatedPartners = [...(formData.contactPartners || []), newPartner];
      setFormData(prev => ({ ...prev, contactPartners: updatedPartners }));
    } catch (error) {
      console.error('Error adding contact partner:', error);
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditContactPartner = async (
    id: string,
    data: ContactPartnerFormData
  ) => {
    setIsLoading(true);
    try {
      const updatedPartners = (formData.contactPartners || []).map(partner =>
        partner.id === id ? { ...partner, ...data } : partner
      );
      setFormData(prev => ({ ...prev, contactPartners: updatedPartners }));
    } catch (error) {
      console.error('Error editing contact partner:', error);
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteContactPartner = async (id: string) => {
    setIsLoading(true);
    try {
      const updatedPartners = (formData.contactPartners || []).filter(
        partner => partner.id !== id
      );
      setFormData(prev => ({ ...prev, contactPartners: updatedPartners }));
    } catch (error) {
      console.error('Error deleting contact partner:', error);
      onError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    {
      id: 'general',
      label: tForms('account.generalHeading'),
      description: tForms('account.generalDescription'),
    },
    {
      id: 'billing',
      label: tForms('account.billingHeading'),
      description: tForms('account.billingDescription'),
    },
    {
      id: 'contract',
      label: tForms('account.contractHeading'),
      description: tForms('account.contractDescription'),
    },
    {
      id: 'contact',
      label: tForms('account.contactHeading'),
      description: tForms('account.contactDescription'),
    },
    {
      id: 'banking',
      label: tForms('account.bankingHeading'),
      description: tForms('account.bankingDescription'),
    },
  ];

  // Tab navigation helpers
  const currentTabIndex = tabs.findIndex(tab => tab.id === activeTab);
  const isLastTab = currentTabIndex === tabs.length - 1;
  const isFirstTab = currentTabIndex === 0;

  // Handle previous tab navigation
  const handlePreviousTab = () => {
    if (isFirstTab) return;
    const previousTabIndex = currentTabIndex - 1;
    setActiveTab(tabs[previousTabIndex].id);
  };

  // Handle next tab navigation
  const handleNextTab = () => {
    if (isLastTab) return;
    const nextTabIndex = currentTabIndex + 1;
    setActiveTab(tabs[nextTabIndex].id);
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsValidating(true);

    try {
      // Get required forms for UI validation
      const requiredFormIds = ['general', 'billing', 'contract', 'banking'];
      let allFormsValid = true;
      let firstInvalidForm = '';

      // Validate general form
      if (requiredFormIds.includes('general')) {
        if (activeTab === 'general') {
          // Use ref validation for active form
          const isValid = await generalFormRef.current?.triggerValidation();
          if (!isValid) {
            allFormsValid = false;
            firstInvalidForm = firstInvalidForm || 'general';
          }
        } else {
          // Use data validation for inactive form
          const isComplete = isFormComplete('general', formData);
          if (!isComplete) {
            allFormsValid = false;
            firstInvalidForm = firstInvalidForm || 'general';
          }
        }
      }

      // Validate billing form
      if (requiredFormIds.includes('billing')) {
        if (activeTab === 'billing') {
          // Use ref validation for active form
          const isValid = await billingFormRef.current?.triggerValidation();
          if (!isValid) {
            allFormsValid = false;
            firstInvalidForm = firstInvalidForm || 'billing';
          }
        } else {
          // Use data validation for inactive form
          const isComplete = isFormComplete('billing', formData);
          if (!isComplete) {
            allFormsValid = false;
            firstInvalidForm = firstInvalidForm || 'billing';
          }
        }
      }
      // Validate contract partners (required)
      if (requiredFormIds.includes('contract')) {
        const isComplete = isFormComplete('contract', formData);
        if (!isComplete) {
          allFormsValid = false;
          firstInvalidForm = firstInvalidForm || 'contract';
        }
      }

      // Validate banking form
      if (requiredFormIds.includes('banking')) {
        if (activeTab === 'banking') {
          // Use ref validation for active form
          const isValid = await bankingFormRef.current?.triggerValidation();
          if (!isValid) {
            allFormsValid = false;
            firstInvalidForm = firstInvalidForm || 'banking';
          }
        } else {
          // Use data validation for inactive form
          const isComplete = isFormComplete('banking', formData);
          if (!isComplete) {
            allFormsValid = false;
            firstInvalidForm = firstInvalidForm || 'banking';
          }
        }
      }

      if (!allFormsValid) {
        // Navigate to the first invalid form
        if (firstInvalidForm) {
          setActiveTab(firstInvalidForm);
        }

        // Show specific error message if contract partners tab is the issue
        if (firstInvalidForm === 'contract') {
          toast({
            title: tForms('account.validationErrorTitle') || 'Validation Error',
            description:
              tForms('account.contractPartnerRequired') ||
              'At least one Contract Partner is required',
            variant: 'destructive',
          });
        } else {
          toast({
            title: tForms('account.validationErrorTitle') || 'Validation Error',
            description:
              tForms('account.validationError') ||
              'Please complete all required forms',
            variant: 'destructive',
          });
        }
        setIsValidating(false);
        return;
      }

      // Adapt form data to API format
      const apiFormData = adaptAccountFormDataForApi(token, formData);

      // Submit to API using the mutation hook
      accountFormSubmission.mutate(apiFormData, {
        onSuccess: (_response: { success: boolean; message: string }) => {
          // Handle successful API submission
          toast({
            title: tForms('account.submitSuccessTitle') || 'Success',
            description:
              tForms('account.submitSuccess') ||
              'Account information submitted successfully',
            variant: 'default',
          });

          // Call the parent's onSubmit callback with local form data
          onSubmit?.(formData);
        },
        onError: (error: Error) => {
          // Handle API submission errors
          console.error('Account form API submission error:', error);
          toast({
            title: tForms('account.submitErrorTitle') || 'Error',
            description:
              error.message || 'Failed to submit account information',
            variant: 'destructive',
          });

          // Call the parent's onError callback
          onError?.(error);
        },
      });
    } catch (error) {
      console.error('Error preparing form data for API:', error);
      toast({
        title: tForms('account.submitErrorTitle') || 'Error',
        description: 'Failed to prepare form data for submission',
        variant: 'destructive',
      });
      onError?.(error as Error);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Card className={className}>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className='w-full'>
          <TabsList className='grid w-full grid-cols-5'>
            {tabs.map((tab, index) => {
              // Get effective validation state for this tab
              let validationState: 'valid' | 'invalid' | 'pending' = 'pending';
              if (tab.id === 'general') {
                validationState = getEffectiveValidationState(
                  generalValidationState,
                  'general'
                );
              } else if (tab.id === 'billing') {
                validationState = getEffectiveValidationState(
                  billingValidationState,
                  'billing'
                );
              } else if (tab.id === 'contract') {
                validationState = getEffectiveValidationState(
                  contractValidationState,
                  'contract'
                );
              } else if (tab.id === 'contact') {
                validationState = getEffectiveValidationState(
                  contactValidationState,
                  'contact'
                );
              } else if (tab.id === 'banking') {
                validationState = getEffectiveValidationState(
                  bankingValidationState,
                  'banking'
                );
              }

              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className='text-xs sm:text-sm flex items-center gap-1'
                >
                  <span className='hidden sm:inline'>{tab.label}</span>
                  <span className='sm:hidden'>{index + 1}</span>
                  {getValidationIcon(validationState)}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value='general' className='mt-6'>
            <div className='space-y-4'>
              <div>
                <h3 className='text-lg font-medium'>
                  {tForms('account.generalHeading')}
                </h3>
                <p className='text-sm text-muted-foreground'>
                  {tForms('account.generalDescription')}
                </p>
              </div>
              <AccountGeneralInfoForm
                ref={generalFormRef}
                token={token}
                onSubmit={handleTabSubmit}
                onError={handleTabError}
                onChange={handleGeneralInfoChange}
                initialData={formData.general}
              />
            </div>
          </TabsContent>

          <TabsContent value='billing' className='mt-6'>
            <div className='space-y-4'>
              <div>
                <h3 className='text-lg font-medium'>
                  {tForms('account.billingHeading')}
                </h3>
                <p className='text-sm text-muted-foreground'>
                  {tForms('account.billingDescription')}
                </p>
              </div>
              <BillingAddressForm
                ref={billingFormRef}
                formData={formData.billing}
                onSubmit={handleTabSubmit}
                onError={handleTabError}
                onChange={handleBillingAddressChange}
              />
            </div>
          </TabsContent>

          <TabsContent value='contract' className='mt-6'>
            <div className='space-y-4'>
              <ContractPartnerManager
                contractPartners={formData.contractPartners || []}
                onAdd={handleAddContractPartner}
                onEdit={handleEditContractPartner}
                onDelete={handleDeleteContractPartner}
                isLoading={isLoading}
              />
            </div>
          </TabsContent>

          <TabsContent value='contact' className='mt-6'>
            <div className='space-y-4'>
              <ContactPartnerManager
                contactPartners={formData.contactPartners || []}
                onAdd={handleAddContactPartner}
                onEdit={handleEditContactPartner}
                onDelete={handleDeleteContactPartner}
                isLoading={isLoading}
              />
            </div>
          </TabsContent>

          <TabsContent value='banking' className='mt-6'>
            <div className='space-y-4'>
              <div>
                <h3 className='text-lg font-medium'>
                  {tForms('account.bankingHeading')}
                </h3>
                <p className='text-sm text-muted-foreground'>
                  {tForms('account.bankingDescription')}
                </p>
              </div>
              <BankConnectionForm
                ref={bankingFormRef}
                onSubmit={handleTabSubmit}
                onChange={handleBankingChange}
                formData={formData.banking}
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Sticky Footer Navigation */}
        <StickyFooter
          onPrevious={!isFirstTab ? handlePreviousTab : undefined}
          onNext={!isLastTab ? handleNextTab : undefined}
          onSubmit={onSubmit ? handleSubmit : undefined}
          showPrevious={!isFirstTab}
          showNext={!isLastTab}
          showSubmit={!!onSubmit}
          isValidating={isValidating}
          isSubmitting={isLoading}
        />
      </CardContent>
    </Card>
  );
}

export default AccountFormTabs;
